import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [deliveryZones, setDeliveryZones] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('gf_cart');
    if (saved) {
      try {
        setItems(JSON.parse(saved));
      } catch (e) {
        localStorage.removeItem('gf_cart');
      }
    }
    fetchDeliveryZones();
  }, []);

  useEffect(() => {
    localStorage.setItem('gf_cart', JSON.stringify(items));
  }, [items]);

  const fetchDeliveryZones = async () => {
    const { data } = await supabase
      .from('delivery_zones')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');
    if (data) setDeliveryZones(data);
  };

  const addItem = (product, quantity = 1) => {
    // Check stock for gourmet products
    if (product.is_gourmet) {
      const currentInCart = items.find(i => i.product.id === product.id)?.quantity || 0;
      if (currentInCart + quantity > product.stock) {
        toast.error(`Solo quedan ${product.stock} unidades disponibles`);
        return;
      }
    }

    setItems(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity }];
    });
    toast.success(`${product.name} agregado al carrito`);
  };

  const removeItem = (productId) => {
    setItems(prev => prev.filter(item => item.product.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    const item = items.find(i => i.product.id === productId);
    if (item?.product.is_gourmet && quantity > item.product.stock) {
      toast.error(`Solo quedan ${item.product.stock} unidades`);
      return;
    }
    setItems(prev =>
      prev.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('gf_cart');
  };

  const subtotal = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Save order to Supabase
  const saveOrder = async (customerInfo, deliveryCost = 0) => {
    const orderTotal = subtotal + deliveryCost;

    const { data: order, error: orderError } = await supabase
      .from('orders')
      .insert({
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email || null,
        delivery_type: customerInfo.deliveryType,
        delivery_zone: customerInfo.deliveryType === 'delivery' ? customerInfo.zone : 'Chonchi',
        delivery_address: customerInfo.address || null,
        delivery_cost: deliveryCost,
        subtotal: subtotal,
        total: orderTotal,
        notes: customerInfo.notes || null,
        b2b_user_id: customerInfo.b2bUserId || null,
        status: 'pending'
      })
      .select()
      .single();

    if (orderError) {
      console.error('Error saving order:', orderError);
      throw orderError;
    }

    // Save order items
    const orderItems = items.map(item => ({
      order_id: order.id,
      product_id: item.product.id,
      product_name: item.product.name,
      product_price: item.product.price,
      quantity: item.quantity,
      subtotal: item.product.price * item.quantity
    }));

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems);

    if (itemsError) {
      console.error('Error saving order items:', itemsError);
    }

    return order;
  };

  const generateWhatsAppMessage = (customerInfo, deliveryCost = 0) => {
    const orderTotal = subtotal + deliveryCost;
    let message = `*Nuevo Pedido - Gama Foods Chiloé*\n\n`;
    message += `*Cliente:* ${customerInfo.name}\n`;
    message += `*Teléfono:* ${customerInfo.phone}\n`;
    message += `*Tipo:* ${customerInfo.deliveryType === 'delivery' ? 'Entrega a domicilio' : 'Retiro en local'}\n`;

    if (customerInfo.deliveryType === 'delivery') {
      message += `*Zona:* ${customerInfo.zone}\n`;
      message += `*Dirección:* ${customerInfo.address}\n`;
      if (deliveryCost > 0) {
        message += `*Costo envío:* $${deliveryCost.toLocaleString('es-CL')}\n`;
      }
    }

    if (customerInfo.notes) {
      message += `*Notas:* ${customerInfo.notes}\n`;
    }

    message += `\n*Productos:*\n`;
    items.forEach(item => {
      message += `- ${item.product.name} x${item.quantity} = $${(item.product.price * item.quantity).toLocaleString('es-CL')}\n`;
    });

    message += `\n*Subtotal: $${subtotal.toLocaleString('es-CL')}*`;
    if (deliveryCost > 0) {
      message += `\n*Envío: $${deliveryCost.toLocaleString('es-CL')}*`;
    }
    message += `\n*Total: $${orderTotal.toLocaleString('es-CL')}*`;

    if (orderTotal >= 15000) {
      message += `\n_Envío GRATIS por compra sobre $15.000_`;
    }

    return encodeURIComponent(message);
  };

  return (
    <CartContext.Provider value={{
      items,
      isOpen,
      setIsOpen,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      subtotal,
      total: subtotal,
      itemCount,
      deliveryZones,
      saveOrder,
      generateWhatsAppMessage
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
